import { supabase } from '@/integrations/supabase/client';
import type { LpaResponseAttachment } from '../types';

const LPA_ATTACHMENT_BUCKET = 'lpa-response-attachments';

function normalizeFileName(fileName: string): string {
  return fileName
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9._-]/g, '');
}

function buildAttachmentPath(params: {
  tenantId: string;
  completionId: string;
  responseId: string;
  fileName: string;
}): string {
  const safeName = normalizeFileName(params.fileName) || 'attachment';
  return `${params.tenantId}/${params.completionId}/${params.responseId}/${Date.now()}-${safeName}`;
}

export async function uploadAttachment(
  responseId: string,
  file: File,
  options?: { tenantId?: string; createdBy?: string | null },
): Promise<LpaResponseAttachment> {
  let responseQuery = supabase
    .schema('core')
    .from('mod_lpa_responses')
    .select('id, tenant_id, completion_id')
    .eq('id', responseId);

  if (options?.tenantId) {
    responseQuery = responseQuery.eq('tenant_id', options.tenantId);
  }

  const { data: response, error: responseError } = await responseQuery.single();
  if (responseError) throw responseError;

  const filePath = buildAttachmentPath({
    tenantId: response.tenant_id,
    completionId: response.completion_id,
    responseId: response.id,
    fileName: file.name,
  });

  const { error: uploadError } = await supabase.storage
    .from(LPA_ATTACHMENT_BUCKET)
    .upload(filePath, file, {
      contentType: file.type || undefined,
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const { data: attachment, error: attachmentError } = await supabase
    .schema('core')
    .from('mod_lpa_response_attachments')
    .insert({
      tenant_id: response.tenant_id,
      response_id: response.id,
      file_path: filePath,
      file_name: file.name,
      file_type: file.type || null,
      created_by: options?.createdBy ?? null,
    })
    .select('*')
    .single();

  if (attachmentError) {
    await supabase.storage.from(LPA_ATTACHMENT_BUCKET).remove([filePath]);
    throw attachmentError;
  }

  return attachment as unknown as LpaResponseAttachment;
}

export async function fetchAttachmentsByResponseId(
  responseId: string,
  tenantId?: string,
): Promise<LpaResponseAttachment[]> {
  let query = supabase
    .schema('core')
    .from('mod_lpa_response_attachments')
    .select('*')
    .eq('response_id', responseId)
    .order('created_at', { ascending: true });

  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as unknown as LpaResponseAttachment[];
}

export async function deleteAttachment(
  attachmentId: string,
  tenantId?: string,
): Promise<void> {
  let attachmentQuery = supabase
    .schema('core')
    .from('mod_lpa_response_attachments')
    .select('id, tenant_id, file_path')
    .eq('id', attachmentId);

  if (tenantId) {
    attachmentQuery = attachmentQuery.eq('tenant_id', tenantId);
  }

  const { data: attachment, error: attachmentError } = await attachmentQuery.single();
  if (attachmentError) throw attachmentError;

  const { error: storageDeleteError } = await supabase.storage
    .from(LPA_ATTACHMENT_BUCKET)
    .remove([attachment.file_path]);

  if (
    storageDeleteError &&
    !storageDeleteError.message.toLowerCase().includes('not found')
  ) {
    throw storageDeleteError;
  }

  let dbDeleteQuery = supabase
    .schema('core')
    .from('mod_lpa_response_attachments')
    .delete()
    .eq('id', attachment.id);

  if (tenantId) {
    dbDeleteQuery = dbDeleteQuery.eq('tenant_id', tenantId);
  }

  const { error: dbDeleteError } = await dbDeleteQuery;
  if (dbDeleteError) throw dbDeleteError;
}

export async function createAttachmentSignedUrl(
  filePath: string,
  expiresInSeconds = 60 * 60,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(LPA_ATTACHMENT_BUCKET)
    .createSignedUrl(filePath, expiresInSeconds);

  if (error) throw error;
  return data.signedUrl;
}
